import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { Menu, X, User, Search, Home, Newspaper, ChevronRight, Twitter, Linkedin, Instagram, Facebook, Youtube, Globe, FileText, Edit3, Image, Layout as LayoutIcon, Users, LogOut, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// ============================================
// LA TECNOLOGÍA LOGO COMPONENT
// Using CSS text instead of image due to whitespace issues in the uploaded logo image
// ============================================
const Logo = ({ className = '', dark = false }) => {
  return (
    <div className={`flex items-center ${className}`}>
      <span className="text-xl md:text-2xl font-bold tracking-[0.12em]">
        <span className={dark ? 'text-white' : 'text-gray-800'}>LA</span>
        <span className="text-cyan-400">TECHNO</span>
        <span className={dark ? 'text-white' : 'text-gray-800'}>LOGÍA</span>
      </span>
    </div>
  );
};

// Logo for footer (smaller)
const FooterLogo = () => {
  return (
    <div className="flex items-center">
      <span className="text-lg font-bold tracking-tight">
        <span className="text-white">LA</span>
        <span className="text-cyan-400">TECH</span>
      </span>
    </div>
  );
};

// ============================================
// MOBILE BOTTOM NAV
// ============================================
const MobileBottomNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  const navItems = [
    { icon: Home, label: 'Inicio', href: '/' },
    { icon: Newspaper, label: 'Noticias', href: '/dossiers' },
    { icon: Search, label: 'Buscar', href: '/search' },
    { icon: User, label: user ? 'Perfil' : 'Entrar', href: user ? '/profile' : '/login' },
  ];

  return (
    <nav 
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#dddddd] safe-area-bottom"
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-center justify-around h-16 px-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href || 
            (item.href !== '/' && location.pathname.startsWith(item.href));
          
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`flex flex-col items-center justify-center w-16 h-14 rounded-lg transition-all ${
                isActive 
                  ? 'text-[#e62d28]' 
                  : 'text-[#555555] hover:text-black'
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <Icon className={`w-5 h-5 ${isActive ? 'stroke-2' : ''}`} />
              <span className={`text-[10px] mt-1 ${isActive ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// ============================================
// HEADER - Clean Light Theme
// ============================================
export const Header = () => {
  const { user, logout, isAdmin, isStaff } = useAuth();
  const { navigation, getActiveNavigation } = useSiteConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const activeNav = getActiveNavigation();
  
  const navItems = activeNav.length > 0 
    ? activeNav.map(item => ({ label: item.label, href: item.url }))
    : [
        { label: 'Noticias', href: '/dossiers' },
        { label: 'Edición Semanal', href: '/edition' },
        { label: 'Biblioteca', href: '/blog' },
      ];

  return (
    <>
      <header className="sticky top-0 z-50 bg-[#000000]" data-testid="main-header">
        <div className="px-6 sm:px-8 lg:px-16">
          {/* Mobile Header */}
          <div className="lg:hidden flex items-center justify-between h-16">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex items-center justify-center w-10 h-10 text-gray-300 hover:text-white transition-colors"
              aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <Link to="/" className="flex-shrink-0" onClick={() => setMobileMenuOpen(false)}>
              <Logo className="scale-90" dark />
            </Link>
            
            <div className="w-10" />
          </div>

          {/* Desktop Header — Daily Wire style: [Logo][Nav] ---gap--- [Search | Login | SIGN UP] */}
          <div className="hidden lg:flex items-center h-[77px]">
            {/* Left group: Logo + Nav links together */}
            <div className="flex items-center gap-8">
              <Link to="/" className="flex-shrink-0">
                <Logo dark />
              </Link>
              <nav className="flex items-center gap-6">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`text-[14px] font-medium transition-colors whitespace-nowrap ${
                      location.pathname === item.href 
                        ? 'text-white' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Right group: Search | Login | SIGN UP */}
            <div className="flex items-center gap-5">
              <Link to="/search" className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-colors">
                <Search className="w-4 h-4" />
                <span className="text-[14px] font-medium">Buscar</span>
              </Link>
              
              {/* Vertical divider */}
              <div className="w-px h-5 bg-gray-600" />
              
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                    <span className="text-[14px] font-medium">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className="w-56 p-1 bg-white border border-gray-200 shadow-lg"
                  >
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      className="flex items-center gap-3 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded px-3 py-2.5 cursor-pointer"
                    >
                      <User className="w-4 h-4" />
                      Mi Perfil
                    </DropdownMenuItem>
                    
                    {isStaff && (
                      <>
                        <DropdownMenuSeparator className="bg-gray-200 my-1" />
                        <div className="px-3 py-1.5">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Editor</span>
                        </div>
                        <DropdownMenuItem 
                          onClick={() => navigate('/editor/my-content')}
                          className="flex items-center gap-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded px-3 py-2.5 cursor-pointer"
                        >
                          <FileText className="w-4 h-4" />
                          Mi Contenido
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/write')}
                          className="flex items-center gap-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded px-3 py-2.5 cursor-pointer"
                        >
                          <Edit3 className="w-4 h-4" />
                          Nueva Noticia
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/gallery')}
                          className="flex items-center gap-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded px-3 py-2.5 cursor-pointer"
                        >
                          <Image className="w-4 h-4" />
                          Galería
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator className="bg-gray-200 my-1" />
                        <div className="px-3 py-1.5">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</span>
                        </div>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin')}
                          className="flex items-center gap-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded px-3 py-2.5 cursor-pointer"
                        >
                          <LayoutIcon className="w-4 h-4" />
                          Panel Editorial
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/staff')}
                          className="flex items-center gap-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded px-3 py-2.5 cursor-pointer"
                        >
                          <Users className="w-4 h-4" />
                          Gestión de Equipo
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    <DropdownMenuSeparator className="bg-gray-200 my-1" />
                    <DropdownMenuItem 
                      onClick={logout} 
                      className="flex items-center gap-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded px-3 py-2.5 cursor-pointer"
                    >
                      <LogOut className="w-4 h-4" />
                      Cerrar Sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link 
                    to="/login"
                    className="text-[14px] text-gray-400 hover:text-white font-medium transition-colors"
                  >
                    LOG IN
                  </Link>
                  <Link 
                    to="/login"
                    className="px-5 py-2 text-[14px] font-bold text-white bg-[#e62d28] hover:bg-[#c9251f] transition-colors"
                    data-testid="signup-btn"
                  >
                    SIGN UP
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* MOBILE MENU */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-14 z-40 overflow-y-auto pb-20 bg-white">
            <div className="px-4 py-4">
              {/* User Section */}
              {user ? (
                <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{user.name}</p>
                      <p className="text-gray-500 text-sm">{user.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Link
                      to="/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center gap-2 text-gray-700 bg-white py-3 px-4 rounded-lg border border-gray-200 transition-colors min-h-[48px]"
                    >
                      Mi Perfil
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 text-white bg-blue-600 py-3 px-4 rounded-lg transition-colors min-h-[48px]"
                      >
                        Admin
                      </Link>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
                  <p className="text-gray-600 text-sm mb-3">Accede a contenido exclusivo</p>
                  <div className="grid grid-cols-1 gap-2">
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center text-gray-700 bg-white py-3 px-4 rounded-lg font-medium border border-gray-200 transition-colors min-h-[48px]"
                    >
                      Iniciar Sesión
                    </Link>
                    {/* HIDDEN: Subscription button - uncomment to restore
                    <Link
                      to="/subscription"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center text-white bg-blue-600 py-3 px-4 rounded-lg font-medium transition-colors min-h-[48px]"
                    >
                      Suscríbete
                    </Link>
                    */}
                  </div>
                </div>
              )}

              {/* Navigation Links */}
              <div className="space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between px-4 py-4 text-gray-800 hover:bg-gray-50 rounded-lg transition-all min-h-[56px]"
                  >
                    <span className="text-base font-medium">{item.label}</span>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </Link>
                ))}
              </div>

              {/* Logout */}
              {user && (
                <div className="mt-8">
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center justify-center py-4 rounded-lg font-medium text-blue-600 bg-blue-50 border border-blue-200 transition-colors min-h-[56px]"
                  >
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      
      <MobileBottomNav />
    </>
  );
};

// ============================================
// FOOTER - Light Theme
// ============================================
export const Footer = () => {
  const { footer } = useSiteConfig();
  const currentYear = new Date().getFullYear();
  
  const socialIcons = {
    twitter: Twitter,
    linkedin: Linkedin,
    instagram: Instagram,
    facebook: Facebook,
    youtube: Youtube,
    website: Globe,
  };
  
  const footerLinks = footer.footer_links?.length > 0 
    ? footer.footer_links 
    : [
        { label: 'Sobre Nosotros', href: '/about' },
        { label: 'Contacto', href: '/contact' },
        { label: 'Privacidad', href: '/privacy' },
        { label: 'Términos', href: '/terms' },
      ];
  
  const socialLinks = footer.social_links || [];
  const companyName = footer.company_name || 'La Tecnología';
  
  return (
    <footer className="bg-gray-900 border-t border-gray-800 pb-20 lg:pb-0">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12">
        {/* Logo & Links */}
        <div className="flex flex-col items-center mb-8">
          <div className="mb-6">
            <FooterLogo />
          </div>
          
          {/* Social Links */}
          {socialLinks.length > 0 && (
            <div className="flex justify-center gap-4 mb-6">
              {socialLinks.map((link, idx) => {
                const Icon = socialIcons[link.platform] || Globe;
                return (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
                    aria-label={link.platform}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          )}
          
          {/* Links */}
          <div className="flex flex-wrap justify-center gap-4 lg:gap-6 text-sm text-gray-400 mb-8">
            {footerLinks.map((link, idx) => (
              <Link 
                key={idx}
                to={link.url || link.href} 
                className="py-2 px-1 hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
        
        {/* Copyright */}
        <div className="text-center text-sm text-gray-500">
          <p>© {currentYear} {companyName}. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// LAYOUT WRAPPER
// ============================================
export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-[#f8f8f8]">
      <Header />
      <main className="flex-grow pb-16 lg:pb-0">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
